import { Checkbox, Slider } from '@nextui-org/react'
import { useEffect, useState } from 'react'
import useAsync from 'react-use/esm/useAsync'
import { SimpleTextarea } from '~/components/common/SimpleTextarea'
import { AgentRequest } from '~/protos/agents'
import { bridge } from '~/services/bridge'
import { TaskParams } from './BaseTaskForm'
import { InfoTooltip } from './shared/InfoToolTip'

interface MunitTestFormProps {
  onChange: (task: TaskParams) => void
  flowName: string | null
  initMunitNotes?: string
  initEnableCoverage?: boolean
  initNumOfScenarios?: number
  initVmArgs?: string
  suggestedVmArgs?: string
  isLoadingSuggestedVmArgs?: boolean
  hideVmArgs?: boolean
  disabled?: boolean
}

export function MunitTestForm({
  onChange,
  flowName,
  initMunitNotes = '',
  initEnableCoverage = true,
  initNumOfScenarios = 1,
  initVmArgs = '',
  suggestedVmArgs = '',
  isLoadingSuggestedVmArgs = false,
  hideVmArgs = false,
  disabled = false
}: MunitTestFormProps) {
  const isPlugin = bridge.isInPlugin()
  const [vmArgs, setVMArgs] = useState<string>(initVmArgs)
  const [enableCoverage, setEnableCoverage] = useState<boolean>(initEnableCoverage)
  const [numOfScenarios, setNumOfScenarios] = useState<number>(initNumOfScenarios)
  const [additionalNotes, setAdditionalNotes] = useState<string>(initMunitNotes)

  // Sync props to state (for edit mode support)
  useEffect(() => {
    setAdditionalNotes(initMunitNotes)
  }, [initMunitNotes])

  useEffect(() => {
    setVMArgs(initVmArgs)
  }, [initVmArgs])

  useEffect(() => {
    setEnableCoverage(initEnableCoverage)
  }, [initEnableCoverage])

  useEffect(() => {
    setNumOfScenarios(initNumOfScenarios)
  }, [initNumOfScenarios])

  // Update onChange when form data changes
  useAsync(async () => {
    if (vmArgs != null || flowName != null) {
      const request = AgentRequest.fromPartial({
        command: flowName ? { commandString: flowName } : undefined,
        vmArgs,
        munitNotes: additionalNotes,
        munitEnableCoverage: enableCoverage,
        munitNumScenarios: numOfScenarios
      })
      onChange?.({ request })
    }
  }, [vmArgs, additionalNotes, numOfScenarios, enableCoverage, flowName])

  return (
    <div className='input-output-form'>
      {/* FlowNameSelector is now in TaskForm, not here */}

      {!hideVmArgs && (
        <div className='description'>
          <div
            className={`tool-tip-target vm-args-textarea w-[1px] h-[1px] relative top-[70px] ${isPlugin ? 'left-[200px]' : 'left-[400px]'}`}
          ></div>
          <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] flex justify-between items-center select-none'>
            <div className='flex items-center gap-2'>
              <span>VM Args:</span>
              {isLoadingSuggestedVmArgs && (
                <div className='flex items-center gap-1 text-xs text-gray-500 select-none'>
                  <svg className='animate-spin h-3 w-3' viewBox='0 0 24 24'>
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                      fill='none'
                    />
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    />
                  </svg>
                  <span>Loading VM args suggestions from repository...</span>
                </div>
              )}
            </div>
            <InfoTooltip content='If running the test requires VM arguments, users can supply them to ensure the tests execute correctly within the appropriate environment settings.' />
          </div>
          <SimpleTextarea
            className='mx-0'
            value={vmArgs}
            onChange={setVMArgs}
            minRows={2}
            placeholder={suggestedVmArgs || ''}
            disabled={disabled}
          />
        </div>
      )}

      <div className={isPlugin ? 'grid grid-cols-1 gap-2' : 'grid grid-cols-2 gap-4'}>
        <div className='coverage-checkbox'>
          <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] flex justify-between items-center select-none'>
            <span>Enable Coverage:</span>
            <InfoTooltip content='Enables code coverage analysis for the generated test cases.' />
          </div>
          <Checkbox
            defaultChecked
            isSelected={enableCoverage}
            onChange={ev => setEnableCoverage(ev.target.checked)}
            isDisabled={disabled}
          />
        </div>

        <div className='num-of-scenarios'>
          <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] flex justify-between items-center select-none'>
            <span>Number of MUnit scenarios (atmost {numOfScenarios}):</span>
            <InfoTooltip content='Specifies the number of test scenarios to generate.' />
          </div>
          <Slider
            value={numOfScenarios}
            onChange={ev => setNumOfScenarios(Number(ev))}
            minValue={1}
            maxValue={3}
            step={1}
            isDisabled={disabled}
          />
        </div>
      </div>

      <div className='description'>
        <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] flex justify-between items-center select-none'>
          <span>Notes (Optional):</span>
          <InfoTooltip content='Generates detailed test cases to validate different conditions within the flow.' />
        </div>
        <SimpleTextarea
          value={additionalNotes}
          onChange={setAdditionalNotes}
          minRows={2}
          placeholder='Additional notes and guiding instructions to help understand your needs better. Please refer to the help article on right to see some examples and tips on how to use this field'
          disabled={disabled}
        />
      </div>
    </div>
  )
}
